import { isEmpty, isNil } from 'lodash';
import { cloneElement, useState, useRef } from 'react';
import { Importer, ImporterField, enUS, ptBR } from 'react-csv-importer';
import { getI18n, useTranslation } from 'react-i18next';
import { Alert } from 'reactstrap';
import 'react-csv-importer/dist/index.css';

const customPtBR = {
  ...ptBR,
  fieldsStep: {
    ...ptBR.fieldsStep,
    nextButton: 'Validar Arquivo',
  },
  progressStep: {
    ...ptBR.progressStep,
    finishButton: 'Importar',
  },
};

const customEnUS = {
  ...enUS,
  fieldsStep: {
    ...enUS.fieldsStep,
    nextButton: 'Validate File',
  },
  progressStep: {
    ...enUS.progressStep,
    finishButton: 'Import',
  },
};

/**
 * Renders react-csv-importer with backend validation
 */
const CsvImporter = ({
  fields,
  onClose,
  onComplete,
  dataHandler,
  restartable = false,
  onFileValidation,
}) => {
  const { t } = useTranslation();
  const [isValidatingFile, setIsValidatingFile] = useState(false);
  const [fileValidationError, setFileValidationError] = useState(null);
  const [backendValidationResult, setBackendValidationResult] = useState(null);
  const count = [];
  const processedRowsCount = useRef(0);

  const handleDataHandler = (rows) => {
    if (rows?.length) {
      count.push(rows?.length);
      processedRowsCount.current += rows.length;
    }
    dataHandler(count);
  };

  const handleComplete = async (data) => {
    if (onFileValidation && data.file) {
      setIsValidatingFile(true);
      setFileValidationError(null);
      setBackendValidationResult(null);

      try {
        const validationResult = await onFileValidation(data.file, data);

        if (validationResult?.error) {
          setFileValidationError(
            validationResult.message || 'Erro na validação do arquivo'
          );
          setIsValidatingFile(false);
          return;
        }

        if (validationResult?.validationData) {
          setBackendValidationResult(validationResult.validationData);

          data.validationData = validationResult.validationData;
          data.detectedColumns =
            validationResult.validationData.detectedColumns;
        }
      } catch (error) {
        setFileValidationError('Erro na validação do arquivo');
        setIsValidatingFile(false);
        return;
      } finally {
        setIsValidatingFile(false);
      }
    }

    onComplete(data);
  };

  const downloadBackendErrors = () => {
    if (backendValidationResult?.errorFileUrl) {
      const link = document.createElement('a');
      link.href = backendValidationResult.errorFileUrl;
      link.download = 'erros_importacao.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const logFileInfo = (file) => {
    console.log(
      'Arquivo selecionado:',
      file.name,
      'tamanho:',
      file.size,
      'bytes'
    );

    if (file.size > 5000000) {
      console.warn(
        'Arquivo muito grande, pode causar problemas de performance'
      );
    }

    return file;
  };

  return (
    <div>
      {fileValidationError && (
        <Alert color="danger" className="mb-4">
          <h5 className="mb-0">{t('file-validation-error')}</h5>
          <p className="mb-0 mt-1">{fileValidationError}</p>
        </Alert>
      )}

      {backendValidationResult?.errorCount > 0 && (
        <Alert color="danger" className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="col-8">
              <h5 className="mb-0">{t('validation-errors-title')}</h5>
              <p className="mb-0 mt-1">
                {backendValidationResult.errorCount} erros encontrados. Por
                favor, baixe o arquivo de erros para verificar os detalhes e
                corrigir os problemas. Os itens com erro não afetam a
                importação, pois serão descartados no fluxo.
              </p>
            </div>
            {backendValidationResult?.errorFileUrl && (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={downloadBackendErrors}
              >
                <i className="ri-download-line me-1"></i>
                {t('download-errors')}
              </button>
            )}
          </div>
        </Alert>
      )}

      {isValidatingFile && (
        <Alert color="info" className="mb-4">
          <div className="d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <span>{t('validating-file')}</span>
          </div>
        </Alert>
      )}

      <Importer
        processFile={logFileInfo}
        newline=""
        delimiter={[',', ';', '\t', '\n']}
        locale={getI18n().language === 'pt' ? customPtBR : customEnUS}
        restartable={restartable}
        defaultNoHeader={false}
        skipEmptyLines={'greedy'}
        chunkSize={1000}
        onClose={onClose}
        onComplete={handleComplete}
        dataHandler={handleDataHandler}
      >
        {fields?.length &&
          fields?.map((props, i) =>
            cloneElement(<ImporterField key={`fI-${i}`} />, props)
          )}
      </Importer>
    </div>
  );
};

export default CsvImporter;
